/**
 * 점검 상세 페이지 (적응형 레이아웃)
 * 디바이스 유형에 따라 최적화된 UI를 제공합니다.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useInspectionDetailPage } from '../../hooks/useInspectionDetailPage';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { ConfirmDialog } from '../../components/inspection/shared/ConfirmDialog';
import {
  InspectionDetailDesktop,
  InspectionDetailFold,
  InspectionDetailPhone,
} from '../../components/inspection/layouts';
import ResolutionPhotoModal from '../../components/modals/ResolutionPhotoModal';
import ActionResultModal from '../../components/modals/ActionResultModal';
import CameraCaptureModal from '../../components/modals/CameraCaptureModal';
import { PhotoUploadSheet } from '../../components/inspection/shared/PhotoUploadSheet';
import { AnalysisRegionModal } from '../../components/inspection/shared/analysis-region/AnalysisRegionModal';
import { normalizeFileUrl } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';
import { InspectionSiteContactsModal } from '../../components/inspection/InspectionSiteContactsModal';
import {
  INSPECTION_OPEN_CONTACTS_PARAM,
  INSPECTION_OPEN_CONTACTS_VALUE,
} from '../../lib/inspections';
import type { OrganizationCategory } from '@safetyhub/shared';
import { Button, Modal, buttonClassName } from '@safetyhub/ui';

export default function InspectionDetailAdaptive() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const layout = useDeviceLayout();
  const { currentMembership } = useAuthStore();
  const companyId = currentMembership?.companyId;
  const queryClient = useQueryClient();
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false);
  // 사진 선택 시트 모드 (모바일 전용)
  const [photoPickerMode, setPhotoPickerMode] = useState<'photo' | 'bestPractice' | null>(null);
  // 현장 담당자 모달 상태
  const [siteContactsModalOpen, setSiteContactsModalOpen] = useState(false);
  /** 「범위 지정 후 다시 분석」 대상 사진 id — 모달은 이 페이지가 **하나만** 들고 있다 */
  const [regionReanalyzePhotoId, setRegionReanalyzePhotoId] = useState<string | null>(null);

  // 점검 시작 직후 리스트/스케줄 페이지에서 넘어온 경우 자동으로 현장 담당자 모달 오픈
  // URL: /inspections/:id?openContacts=1
  useEffect(() => {
    if (searchParams.get(INSPECTION_OPEN_CONTACTS_PARAM) === INSPECTION_OPEN_CONTACTS_VALUE) {
      setSiteContactsModalOpen(true);
      // param 제거 (뒤로가기 시 다시 열리지 않도록)
      const next = new URLSearchParams(searchParams);
      next.delete(INSPECTION_OPEN_CONTACTS_PARAM);
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // 모바일용 분리 input ref
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const bpCameraInputRef = useRef<HTMLInputElement>(null);
  const bpGalleryInputRef = useRef<HTMLInputElement>(null);

  // 페이지 데이터 훅
  const pageData = useInspectionDetailPage(id);

  // 카메라 권한 확인 후 촬영 모달 열기
  const handleOpenCameraWithPermission = useCallback(async () => {
    try {
      // 카메라 권한 테스트 (스트림은 즉시 해제)
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      stream.getTracks().forEach((t) => t.stop());
      setCameraPermissionDenied(false);
      pageData.onOpenCamera();
    } catch {
      setCameraPermissionDenied(true);
    }
  }, [pageData.onOpenCamera]);

  // 사진 추가: 데스크탑은 갤러리 바로, 모바일은 선택 시트
  const handleAddPhoto = useCallback(() => {
    if (layout === 'desktop') {
      galleryInputRef.current?.click();
    } else {
      setPhotoPickerMode('photo');
    }
  }, [layout]);

  // 우수사례 사진 추가: 데스크탑은 갤러리 바로, 모바일은 선택 시트
  const handleAddBestPracticePhoto = useCallback(() => {
    if (layout === 'desktop') {
      bpGalleryInputRef.current?.click();
    } else {
      setPhotoPickerMode('bestPractice');
    }
  }, [layout]);

  // 사진 메타 업데이트: QueryCache에서 해당 사진 필드만 교체
  const handlePhotoMetaUpdate = useCallback((
    photoId: string,
    updates: {
      location?: { id: string; name: string } | null;
      detailLocation?: { id: string; name: string } | null;
      equipment?: { id: string; name: string; managementNumber?: string | null; processType?: string | null } | null;
      processType?: string | null;
    },
  ) => {
    queryClient.setQueryData(['inspection', id], (old: any) => {
      if (!old?.photos) return old;
      return {
        ...old,
        photos: old.photos.map((p: any) =>
          p.id === photoId ? { ...p, ...updates } : p,
        ),
      };
    });
  }, [queryClient, id]);

  // URL 파라미터로 스크롤 처리 (검토 요청 알림에서 이동 시)
  useEffect(() => {
    const scrollTo = searchParams.get('scrollTo');
    if (scrollTo === 'pendingReview' && pageData.inspection && !pageData.isLoading) {
      // 약간의 지연 후 스크롤 (DOM 렌더링 완료 대기)
      const timeoutId = setTimeout(() => {
        const pendingReviewPhoto = document.querySelector('[data-pending-review="true"]');
        if (pendingReviewPhoto) {
          pendingReviewPhoto.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          // 스크롤 후 하이라이트 효과
          pendingReviewPhoto.classList.add('ring-2', 'ring-orange-400', 'ring-offset-2', 'rounded-card');
          setTimeout(() => {
            pendingReviewPhoto.classList.remove('ring-2', 'ring-orange-400', 'ring-offset-2', 'rounded-card');
          }, 3000);
        }

        // URL 파라미터 제거 (히스토리 교체)
        searchParams.delete('scrollTo');
        setSearchParams(searchParams, { replace: true });
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [searchParams, setSearchParams, pageData.inspection, pageData.isLoading]);

  if (!companyId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-ink-500">조직을 선택해주세요.</p>
      </div>
    );
  }

  // 로딩 상태
  if (pageData.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600 mx-auto" />
          <p className="mt-4 text-ink-500">점검 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // 에러 표시
  if (pageData.error && !pageData.inspection) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500 mb-4">{pageData.error}</p>
          <Link to="/inspections" className={buttonClassName('primary')}>
            목록으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  // 점검 데이터가 없음
  if (!pageData.inspection) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-ink-500 mb-4">점검을 찾을 수 없습니다.</p>
          <Link to="/inspections" className={buttonClassName('primary')}>
            목록으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  // 공통 props
  const commonProps = {
    inspection: pageData.inspection,
    currentUserId: pageData.currentUserId,
    isOwner: pageData.isOwner,
    isPrimary: pageData.isPrimary,
    inspectorId: pageData.inspectorId,
    isReadonly: pageData.isReadonly,
    hasReport: pageData.hasReport,
    isLockedByLaterInspection: pageData.isLockedByLaterInspection,
    enableAIAnalysis: pageData.enableAIAnalysis,
    setEnableAIAnalysis: pageData.setEnableAIAnalysis,
    focusCategories: pageData.focusCategories,
    setFocusCategories: pageData.setFocusCategories,
    onAddPhoto: handleAddPhoto,
    onAddBestPracticePhoto: handleAddBestPracticePhoto,
    onPhotoReview: pageData.onPhotoReview,
    onSelectiveReview: pageData.onSelectiveReview,
    onSelectiveDelete: pageData.onSelectiveDelete,
    onBulkReanalyze: pageData.onBulkReanalyze,
    onDeleteInspection: pageData.onDeleteInspection,
    onResolveFindings: pageData.onResolveFindings,
    onUnresolveFindings: pageData.onUnresolveFindings,
    onRequestResolutionPhoto: pageData.onRequestResolutionPhoto,
    onPhotoDelete: pageData.onPhotoDelete,
    onPhotoReanalyze: pageData.onPhotoReanalyze,
    // 범위 지정 후 재분석 — 레이아웃은 **모달을 열지 않고 대상만 지목**한다. 모달이 이 페이지에
    // 하나만 있으므로 세 레이아웃(desktop/fold/phone)에 사본이 생기지 않는다.
    onPhotoReanalyzeWithRegion: setRegionReanalyzePhotoId,
    onPhotoDownload: pageData.onPhotoDownload,
    onBack: pageData.onBack,
    isLoading: pageData.isBulkReviewLoading || pageData.isDeleting,
    isReanalyzing: pageData.isReanalyzing,
    findingEditHandlers: pageData.findingEditHandlers,
    viewers: pageData.viewers,
    // 검토 요청
    onRequestReview: pageData.onRequestReview,
    // 발견사항 추가
    onAddFinding: pageData.onAddFinding,
    // 발견사항 보고서 포함 선택 (읽기 전용 모드에서는 비활성화)
    isScheduled: pageData.isScheduled,
    findingSelectionEnabled: pageData.isReadonly ? false : pageData.findingSelectionEnabled,
    onToggleMeasureSelection: pageData.isReadonly ? undefined : pageData.onToggleMeasureSelection,
    // 점검완료 서명 워크플로우
    completion: pageData.completion,
    // 예약 점검 시작
    onStartScheduled: pageData.onStartScheduled,
    // 사진 메타 편집
    onPhotoMetaUpdate: handlePhotoMetaUpdate,
    // 현장 담당자 모달 오픈
    onOpenSiteContactsModal: () => setSiteContactsModalOpen(true),
  };

  // 레이아웃별 렌더링
  const renderLayout = () => {
    switch (layout) {
      case 'desktop':
        return <InspectionDetailDesktop {...commonProps} />;
      case 'fold':
        return <InspectionDetailFold {...commonProps} />;
      case 'phone':
        return (
          <InspectionDetailPhone
            {...commonProps}
            onRefresh={pageData.onRefresh}
          />
        );
    }
  };

  return (
    <>
      {/* 읽기 전용 접근 배너 */}
      {pageData.isViewOnlyAccess && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white text-center py-2 text-sm font-medium shadow-md">
          읽기 전용 모드 - 담당 현장이 아니므로 수정할 수 없습니다
        </div>
      )}

      {/* 읽기 전용 모드일 때 상단 여백 추가 */}
      <div className={pageData.isViewOnlyAccess ? 'pt-10' : ''}>
        {renderLayout()}
      </div>

      {/* 점검 시작 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={pageData.startScheduledConfirmOpen}
        onClose={pageData.onCancelStartScheduled}
        onConfirm={async () => {
          await pageData.onConfirmStartScheduled();
          // 점검 시작 성공 후 현장 담당자 모달 자동 오픈
          setSiteContactsModalOpen(true);
        }}
        title="점검 시작"
        message="이 점검을 시작하시겠습니까?"
        confirmText="시작"
        cancelText="취소"
        confirmStyle="primary"
      />

      {/* 숨겨진 파일 입력 (사진 업로드용 - 카메라/갤러리 분리) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={pageData.onPhotoUpload}
        className="hidden"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={pageData.onPhotoUpload}
        className="hidden"
      />

      {/* 숨겨진 파일 입력 (우수사례 사진 업로드용 - 카메라/갤러리 분리) */}
      <input
        ref={bpCameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={pageData.onBestPracticePhotoUpload}
        className="hidden"
      />
      <input
        ref={bpGalleryInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={pageData.onBestPracticePhotoUpload}
        className="hidden"
      />

      {/* 에러 토스트 (있는 경우)
          ⚠️ **`role="alert"`가 이 토스트의 전달 경로다.** 여기 실리는 문구 중에는 **화면에
          미리 보여 줄 수 없는 것**이 있다 — 「사진이 삭제되어 발견사항을 복원할 수 없습니다」가
          그렇다(서버 `restoreFinding`의 사진 생존 게이트). 그 게이트의 도달 경로는 **다른 탭·
          다른 사람이 사진을 지운 stale 화면**이고, 서버 `findOne`은 photos를
          `visiblePhotoWhere()`로 걸러 내려주므로 클라이언트는 그 사실을 **알 방법이 없다** ⇒
          사전 disable이 구조적으로 불가하고, 사후 안내가 유일한 경로다. 그래서 그 한 번을
          놓치면 사용자는 「눌렀는데 아무 일도 없었다」로 끝난다. */}
      {pageData.error && (
        <div className="fixed bottom-20 left-4 right-4 z-50" role="alert" aria-live="assertive">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-card shadow-lg">
            {pageData.error}
          </div>
        </div>
      )}

      {/* 해결 사진 업로드 모달 */}
      <ResolutionPhotoModal onSuccess={pageData.onRefresh} />

      {/* 이전 발견사항 조치결과 모달 */}
      <ActionResultModal onSuccess={pageData.onRefresh} />

      {/* 카메라 촬영 모달 */}
      <CameraCaptureModal onConfirm={pageData.onCameraPhotosUpload} />

      {/* 사진 업로드 시트 (사진 선택 후 메타 설정 → 업로드) */}
      {companyId && pageData.inspection && (
        <PhotoUploadSheet
          isOpen={pageData.photoUploadSheetOpen}
          onClose={pageData.onPhotoUploadSheetClose}
          pendingPhotos={pageData.pendingPhotos}
          onPendingPhotosChange={pageData.onPendingPhotosChange}
          onConfirmUpload={pageData.onConfirmUpload}
          onAddMoreFiles={() => galleryInputRef.current?.click()}
          onOpenCamera={layout !== 'desktop' ? handleOpenCameraWithPermission : undefined}
          isUploading={pageData.isUploading}
          siteId={pageData.inspection.siteId}
          companyId={companyId}
          isDesktop={layout === 'desktop'}
        />
      )}

      {/* 현장 담당자 모달 */}
      {companyId && pageData.inspection && siteContactsModalOpen && (
        <InspectionSiteContactsModal
          inspectionId={pageData.inspection.id}
          siteId={pageData.inspection.siteId}
          companyId={companyId}
          organizationType={
            (currentMembership?.company.category ?? null) as OrganizationCategory | null
          }
          open={siteContactsModalOpen}
          onClose={() => setSiteContactsModalOpen(false)}
          onSkip={() => setSiteContactsModalOpen(false)}
        />
      )}

      {/* 범위 지정 후 다시 분석 — 저장된 영역에서 시작하고(사진 응답이 analysisRegion을 싣는다),
          확인하면 그 영역으로 재분석이 **즉시** 돈다. 그래서 되돌릴 수 없다는 경고를 띄운다. */}
      {(() => {
        const target = regionReanalyzePhotoId
          ? pageData.inspection?.photos.find((p) => p.id === regionReanalyzePhotoId)
          : undefined;
        if (!target) return null;
        return (
          <AnalysisRegionModal
            isOpen
            onClose={() => setRegionReanalyzePhotoId(null)}
            imageUrl={normalizeFileUrl(target.url)}
            initialRegion={target.analysisRegion ?? null}
            confirmLabel="이 범위로 다시 분석"
            warning="다시 분석하면 이 사진의 기존 발견사항이 모두 삭제되고 새로 만들어집니다. 직접 수정한 내용과 선택해 둔 개선대책도 함께 사라집니다."
            onConfirm={(region) => {
              // 영역을 그대로 넘긴다 — `null`은 「해제하고 전체 분석」이라는 사용자 선택이므로
              // 「변경 없음」으로 바꿔 읽으면 안 된다.
              pageData.onPhotoReanalyze(target.id, { analysisRegion: region });
              setRegionReanalyzePhotoId(null);
            }}
          />
        );
      })()}

      {/*
        사진 선택 시트 (모바일 전용) — 어느 폭에서도 아래에 붙는 진짜 바텀시트다(`items-end`·
        `rounded-t-card-lg`, `sm:items-center` 같은 가운데 분기 없음). base에 시트 갈래가 없어
        **줄 단위** disable로 둔다 — 파일째 면제하면 이 파일에 새로 생기는 가운데 모달까지 감시 밖으로
        나간다(`eslint.config.js` `RAW_OVERLAY_EXEMPT` docblock의 「섞인 파일」 경고. 2026-10-29까지
        이 파일이 파일째 면제였고 그 사이 가운데 모달인 카메라 권한 안내가 감시 밖에 있었다).
      */}
      {photoPickerMode && (
        // eslint-disable-next-line local/no-raw-overlay -- 바텀시트 갈래(base에 시트가 없다 — RAW_OVERLAY_EXEMPT docblock의 NOT_A_MODAL 판정)
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center"
          // eslint-disable-next-line local/no-raw-modal -- 같은 바텀시트(위 줄과 같은 판정)
          role="dialog"
          aria-modal="true"
          aria-label="사진 추가 방법 선택"
          onKeyDown={(e) => { if (e.key === 'Escape') setPhotoPickerMode(null); }}
        >
          <div className="absolute inset-0 bg-black/40" onClick={() => setPhotoPickerMode(null)} />
          <div className="relative w-full max-w-md bg-white rounded-t-card-lg p-4 pb-8 space-y-2">
            <div className="w-10 h-1 bg-ink-300 rounded-full mx-auto mb-3" />
            <button
              type="button"
              onClick={() => {
                const ref = photoPickerMode === 'bestPractice' ? bpCameraInputRef : cameraInputRef;
                setPhotoPickerMode(null);
                ref.current?.click();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-card-sm hover:bg-surface-soft active:bg-surface-hover"
            >
              <svg className="w-6 h-6 text-ink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
              </svg>
              <span className="text-base font-medium text-ink-900">카메라 촬영</span>
            </button>
            <button
              type="button"
              onClick={() => {
                const ref = photoPickerMode === 'bestPractice' ? bpGalleryInputRef : galleryInputRef;
                setPhotoPickerMode(null);
                ref.current?.click();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-card-sm hover:bg-surface-soft active:bg-surface-hover"
            >
              <svg className="w-6 h-6 text-ink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
              </svg>
              <span className="text-base font-medium text-ink-900">갤러리에서 선택</span>
            </button>
            <button
              type="button"
              onClick={() => setPhotoPickerMode(null)}
              className="w-full px-4 py-3 text-center rounded-card-sm text-ink-500 hover:bg-surface-soft active:bg-surface-hover"
            >
              취소
            </button>
          </div>
        </div>
      )}

      {/*
        카메라 권한 거부 안내 — base Modal이 오버레이·포커스 가둠/복원·스크롤 잠금·`role="dialog"`를 소유한다.
        입력이 없는 안내라 Esc·바깥 클릭도 「닫기」 버튼과 같은 일(안내만 닫음)을 한다.
        `z-[9998]`은 종전 인라인 값 그대로다 — 이 안내는 `PhotoUploadSheet`(z-[9997]) 안의 카메라 버튼에서
        열리므로 그 위에 떠야 하고, 클래스로 넘겨야 base 레지스트리가 이 창을 「최상단」으로 읽어
        Esc가 아래 시트까지 닫지 않는다(`PhotoUploadSheet`의 Modal 주석과 같은 판정).
      */}
      <Modal
        open={cameraPermissionDenied}
        onClose={() => setCameraPermissionDenied(false)}
        ariaLabel="카메라 권한 필요"
        showCloseButton={false}
        size="sm"
        overlayZClassName="z-[9998]"
      >
          <div>
            <div className="text-center mb-4">
              <svg className="w-12 h-12 mx-auto mb-3 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h3 className="text-lg font-semibold text-ink-900 mb-2">카메라 권한 필요</h3>
              <p className="text-sm text-ink-600 leading-relaxed">
                카메라 사용이 차단되어 있습니다.
                <br />
                브라우저 설정에서 카메라 권한을 허용한 뒤
                <br />
                다시 시도해 주세요.
              </p>
            </div>
            <div className="text-xs text-ink-400 bg-surface-soft rounded-card p-3 mb-4">
              <p className="font-medium text-ink-500 mb-1">권한 허용 방법:</p>
              <p>주소창 왼쪽 자물쇠 아이콘 &gt; 카메라 &gt; 허용</p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="lg"
                className="flex-1"
                onClick={() => setCameraPermissionDenied(false)}
              >
                닫기
              </Button>
              <Button
                type="button"
                variant="primary"
                size="lg"
                className="flex-1"
                onClick={() => {
                  setCameraPermissionDenied(false);
                  handleOpenCameraWithPermission();
                }}
              >
                다시 시도
              </Button>
            </div>
          </div>
      </Modal>
    </>
  );
}
